import { IOwnershipMode, IOwnershipModalSplit } from './../../types/city';
import { getFuelProportions } from './byFuel';
// Calculating Supply
// Spreadsheet name: "Market Size" / EV Stock Sheets

import { zipWith } from 'lodash';
import { createSelector } from 'reselect'

import { IAppState } from './../../reducers/index';
import { IScenarioProps } from './../../types/scenarios';

import { getUtilizationSeries } from './../utilization';
import { getVKTByOwner } from './../vehicleDistanceTraveled';

import {
  getNumProjectionYears,
  getPrivateElectrificationVehicleGoal,
  getPublicCommercialElectrificationVehicleGoal,
} from '../../selectors';

import {
  simpleGrowthRateSeries
} from './../../../services/statistics';

import { debugFuel } from './byFuel';

const BILLION = Math.pow(10, 9); // adjust for BILLION kilometers

// util function
export const getVehicleMarketStock = createSelector(
  [getVKTByOwner, getUtilizationSeries],
  (distanceSeries, utilizationSeries) => {

    const series = zipWith(distanceSeries, utilizationSeries, (distance, utilization) => {
      return {
        autoRickshaw: {
          commercial: distance.autoRickshaw.commercial * BILLION / utilization.autoRickshaw.commercial,
        },
        car: {
          commercial: distance.car.commercial * BILLION / utilization.car.commercial,
          private: distance.car.private * BILLION / utilization.car.private,
          public: 0, // not included
        },
        publicTransit: {
          bus: distance.publicTransit.bus * BILLION / utilization.publicTransit.bus,
          metro: distance.publicTransit.metro * BILLION / utilization.publicTransit.metro,
        },
        twoWheel: {
          commercial: distance.twoWheel.commercial * BILLION / utilization.twoWheel.commercial,
          private: distance.twoWheel.private * BILLION / utilization.twoWheel.private,
        },
      }
    });
    return series;
  }
)

export const getVehicleDemand = getVehicleMarketStock;


// ICE AND EV Stocks
export const getVehicleTotalStock = createSelector(
  [getVehicleMarketStock],
  (marketStock) => {
    // copy the leading year, and remove the last year
    const series = marketStock.slice();
    series.unshift(marketStock[0]);
    series.pop(); // remove the last item

    return series;
  }
)

// Get growth for public/private growth using a shared function
const makeGetElectricGrowthSeries = (goalGetter: (state: IAppState, props: IScenarioProps) => number) => createSelector(
  [
    goalGetter,
    getNumProjectionYears
  ],
  (goal, numYears) => {
    const growthStep = (goal / numYears) * .01; // goal came in as out of 100
    const initialPercent = 0;
    return simpleGrowthRateSeries(initialPercent, growthStep, numYears);
  }
)
const getElectricGrowthPrivateSeries = makeGetElectricGrowthSeries(getPrivateElectrificationVehicleGoal);
const getElectricGrowthPublicSeries = makeGetElectricGrowthSeries(getPublicCommercialElectrificationVehicleGoal);


// get ICE stock, get EV stock will be separate selectors
export const getVehicleElectricStock = createSelector(
  [getVehicleTotalStock,
   getElectricGrowthPrivateSeries,
   getElectricGrowthPublicSeries],
  (stockSeries, privateGrowthSeries, publicGrowthSeries) => {
    return zipWith(stockSeries, privateGrowthSeries, publicGrowthSeries, (stock, privateGrowth, publicGrowth) => {
      return {
        autoRickshaw: {
          commercial: stock.autoRickshaw.commercial * publicGrowth,
        },
        car: {
          commercial: stock.car.commercial * publicGrowth,
          private: stock.car.private * privateGrowth,
          public: 0, // not included
        },
        publicTransit: {
          bus: stock.publicTransit.bus * publicGrowth,
          // metro: 0, // not included in the projections
        },
        twoWheel: {
          commercial: stock.twoWheel.commercial * publicGrowth,
          private: stock.twoWheel.private * privateGrowth,
        },
      }
    });
  }
)

// ICE (Internal Combustion Engine is the total minus the electric quantity )
export const getVehicleICEStock = createSelector(
  [getVehicleTotalStock,
   getVehicleElectricStock],
  (totalStockSeries, electricStockSeries) => {

    return zipWith(totalStockSeries, electricStockSeries, (totalStock, electricStock) => {
      return {
        autoRickshaw: {
          commercial: totalStock.autoRickshaw.commercial - electricStock.autoRickshaw.commercial,
        },
        car: {
          commercial: totalStock.car.commercial - electricStock.car.commercial,
          private: totalStock.car.private - electricStock.car.private,
          // public: 0, // not included
        },
        publicTransit: {
          bus: totalStock.publicTransit.bus - electricStock.publicTransit.bus,
          // metro: 0, // I guess these aren't included
        },
        twoWheel: {
          commercial: totalStock.twoWheel.commercial - electricStock.twoWheel.commercial,
          private: totalStock.twoWheel.private - electricStock.twoWheel.private,
        },
      }
    });
  }
)

// Make 1 selector for each fuel type
// Observe that this is dependent on scenario, since Vehicle Stock is scenario specific
// Functions are copy pasted while we figure out what the right abstraction is...
export const getVehicleICEStockGasoline = createSelector(
  [
    getVehicleICEStock,
    getFuelProportions
  ],
  (totalStockSeries, fuelMapping) => {

    const fuelType = 'gasoline';
    const carPrivateSeries = fuelMapping.car.private[fuelType];
    const carCommercialSeries = fuelMapping.car.commercial[fuelType];
    const autoRickshawSeries = fuelMapping.autoRickshaw.commercial[fuelType];
    const twoWheelSeries = fuelMapping.twoWheel.commercial[fuelType];
    const busSeries = fuelMapping.publicTransit.bus[fuelType];

    return zipWith(
        totalStockSeries,
        carPrivateSeries,
        carCommercialSeries,
        autoRickshawSeries,
        twoWheelSeries,
        busSeries,
      (totalStock: any, // (IOwnershipMode)
        carPrivate: number,
        carCommercial: number,
        autoRickshaw: number,
        twoWheel: number,
        bus: number) => {

      // Need to scale everything down by 100
      return {
        autoRickshaw: {
          commercial: totalStock.autoRickshaw.commercial * autoRickshaw * .01,
        },
        car: {
          commercial: totalStock.car.commercial * carCommercial * .01,
          private: totalStock.car.private * carPrivate * .01,
          // public: 0, // not included
        },
        publicTransit: {
          bus: totalStock.publicTransit.bus * bus * .01,
          // metro: 0, // I guess these aren't included
        },
        twoWheel: {
          commercial: totalStock.twoWheel.commercial * twoWheel * .01,
          private: totalStock.twoWheel.private * twoWheel * .01,
        },
      }
    });
  }
)

export const getVehicleICEStockDiesel = createSelector(
  [
    getVehicleICEStock,
    getFuelProportions
  ],
  (totalStockSeries, fuelMapping) => {

    const fuelType = 'diesel';
    const carPrivateSeries = fuelMapping.car.private[fuelType];
    const carCommercialSeries = fuelMapping.car.commercial[fuelType];
    const autoRickshawSeries = fuelMapping.autoRickshaw.commercial[fuelType];
    const twoWheelSeries = fuelMapping.twoWheel.commercial[fuelType];
    const busSeries = fuelMapping.publicTransit.bus[fuelType];

    return zipWith(
      totalStockSeries,
      carPrivateSeries,
      carCommercialSeries,
      autoRickshawSeries,
      twoWheelSeries,
      busSeries,
      (totalStock: any, // (IOwnershipMode)
        carPrivate: number,
        carCommercial: number,
        autoRickshaw: number,
        twoWheel: number,
        bus: number) => {

        // Need to scale everything down by 100
        return {
          autoRickshaw: {
            commercial: totalStock.autoRickshaw.commercial * autoRickshaw * .01,
          },
          car: {
            commercial: totalStock.car.commercial * carCommercial * .01,
            private: totalStock.car.private * carPrivate * .01,
          },
          publicTransit: {
            bus: totalStock.publicTransit.bus * bus * .01,
          },
          twoWheel: {
            commercial: totalStock.twoWheel.commercial * twoWheel * .01,
            private: totalStock.twoWheel.private * twoWheel * .01,
          },
        }
      });
  }
)

export const getVehicleICEStockCNG = createSelector(
  [
    getVehicleICEStock,
    getFuelProportions
  ],
  (totalStockSeries, fuelMapping) => {

    const fuelType = 'cng';
    const carPrivateSeries = fuelMapping.car.private[fuelType];
    const carCommercialSeries = fuelMapping.car.commercial[fuelType];
    const autoRickshawSeries = fuelMapping.autoRickshaw.commercial[fuelType];
    const twoWheelSeries = fuelMapping.twoWheel.commercial[fuelType];
    const busSeries = fuelMapping.publicTransit.bus[fuelType];

    return zipWith(
      totalStockSeries,
      carPrivateSeries,
      carCommercialSeries,
      autoRickshawSeries,
      twoWheelSeries,
      busSeries,
      (totalStock: any, // (IOwnershipMode)
        carPrivate: number,
        carCommercial: number,
        autoRickshaw: number,
        twoWheel: number,
        bus: number) => {

        // Need to scale everything down by 100
        return {
          autoRickshaw: {
            commercial: totalStock.autoRickshaw.commercial * autoRickshaw * .01,
          },
          car: {
            commercial: totalStock.car.commercial * carCommercial * .01,
            private: totalStock.car.private * carPrivate * .01,
          },
          publicTransit: {
            bus: totalStock.publicTransit.bus * bus * .01,
          },
          twoWheel: {
            commercial: totalStock.twoWheel.commercial * twoWheel * .01,
            private: totalStock.twoWheel.private * twoWheel * .01,
          },
        }
      });
  }
)

// Have to put this here because of circular dependencies
// Utilization By Fuel Type (AKA VKT)
const makeGetUtilizationByFuel = (fuelStockGetter: (state: IAppState, props: IScenarioProps) => any) => createSelector(
  [
    getUtilizationSeries,
    fuelStockGetter
  ],
  (utilizationSeries, stockSeries) => {

    return zipWith(
      utilizationSeries,
      stockSeries,
      (utilization: IOwnershipMode, stock: IOwnershipMode) => {
        return {
          autoRickshaw: {
            commercial: utilization.autoRickshaw.commercial * stock.autoRickshaw.commercial / BILLION,
          },
          car: {
            commercial: utilization.car.commercial * stock.car.commercial / BILLION,
            private: utilization.car.private * stock.car.private / BILLION,
          },
          publicTransit: {
            bus: utilization.publicTransit.bus * stock.publicTransit.bus / BILLION
          },
          twoWheel: {
            commercial: utilization.twoWheel.commercial * stock.twoWheel.commercial / BILLION,
            private: utilization.twoWheel.private * stock.twoWheel.private / BILLION
          },
        }
      });
  }
)
// This is the same thing as VKT
export const getUtilizationGasoline = makeGetUtilizationByFuel(getVehicleICEStockGasoline);
// CNG and Diesel are getting a few more props than needed, but we'll just ignore the extra.
export const getUtilizationCNG = makeGetUtilizationByFuel(getVehicleICEStockCNG);
export const getUtilizationDiesel = makeGetUtilizationByFuel(getVehicleICEStockDiesel);



// WEBPACK FOOTER //
// ./src/store/selectors/vehicleStock/index.ts